/**
 * Stage 1 — the continent. A domain-warped fBm field is added to a soft falloff towards the board
 * edge; the land/sea threshold is then picked by quantile so the continent always covers the
 * target share of the board. Afterwards the land is made a single connected mass (stray islands
 * sink) and the water is classified into deep Ocean, a Shallows ring along the coast, and Lakes
 * (small enclosed water bodies).
 */
import type { HexGrid } from '../core/hex';
import type { Rng } from '../core/rng';
import { Terrain } from '../core/types';
import { type MapContext, smoothstep } from './context';
import { Noise, warp } from './noise';
import { bfs } from './paths';

/** Width of the board-edge ring that is always ocean. */
const BORDER = 2;
/** Enclosed water bodies up to this many hexes become lakes; bigger ones stay (inland) sea. */
const MAX_ENCLOSED_LAKE = 14;
/** Enclosed puddles up to this size are filled in as land (they read as noise, not lakes). */
const MAX_PUDDLE = 2;
/** An inland sea is rejected if it would cut off more than this share of the land. */
const MAX_LAND_LOSS = 0.04;

export function shapeLandmass(ctx: MapContext, rng: Rng): void {
  const { grid, n, ux, uy, extentX: W, extentY: H } = ctx;
  const minDim = Math.min(W, H);
  const warpX = new Noise(rng);
  const warpY = new Noise(rng);
  const shape = new Noise(rng);
  const shelf = new Noise(rng);
  const seaWarpX = new Noise(rng);
  const seaWarpY = new Noise(rng);

  const border = new Uint8Array(n);
  let interior = 0;
  for (let h = 0; h < n; h++) {
    const col = grid.col(h);
    const row = grid.row(h);
    if (col < BORDER || row < BORDER || col >= grid.width - BORDER || row >= grid.height - BORDER)
      border[h] = 1;
    else interior++;
  }
  // Leave room for bays: the interior ring is the hard ceiling, keep ≥ 7 % of it as water.
  const target = Math.min(rng.range(0.66, 0.72), interior / n - 0.07);

  // Continent score: high in the middle, noisy towards the coast.
  const base = new Float64Array(n);
  const falloffWidth = 0.34 * minDim;
  const warpFreq = 1.6 / minDim;
  const warpAmp = 0.14 * minDim;
  const shapeFreq = 2.2 / minDim;
  for (let h = 0; h < n; h++) {
    if (border[h]) continue;
    const e = Math.min(ux[h], W - ux[h], uy[h], H - uy[h]);
    const [px, py] = warp(warpX, warpY, ux[h], uy[h], warpFreq, warpAmp);
    base[h] = smoothstep(1.2, falloffWidth, e) + 0.8 * shape.fbm(px * shapeFreq, py * shapeFreq, 5);
  }

  // Optional inland sea: a warped depression somewhere in the middle third of the board.
  const depression = new Float64Array(n);
  const wantSea = rng.chance(n >= 2500 ? 0.45 : 0.25);
  const sx = rng.range(0.3, 0.7) * W;
  const sy = rng.range(0.3, 0.7) * H;
  const sr = rng.range(0.1, 0.15) * minDim;
  for (let h = 0; h < n; h++) {
    const [px, py] = warp(seaWarpX, seaWarpY, ux[h], uy[h], 0.25, 0.35 * sr);
    const u2 = ((px - sx) * (px - sx) + (py - sy) * (py - sy)) / (sr * sr);
    if (u2 < 1) depression[h] = (1 - u2) * (1 - u2);
  }

  let land: Uint8Array | null = null;
  if (wantSea) {
    const score = base.map((v, h) => v - 1.4 * depression[h]);
    const attempt = buildLand(grid, score, border, interior, target);
    if (attempt.lost <= MAX_LAND_LOSS) land = attempt.land;
  }
  land ??= buildLand(grid, base, border, interior, target).land;

  classifyWater(ctx, land, border, shelf);
}

/**
 * Thresholds `score` so ≈`target` of the board is land, then removes puddles and every land mass
 * but the largest. Re-thresholds a few times because the clean-up shifts the land share.
 * `lost` = share of thresholded land that was discarded as separate islands.
 */
function buildLand(
  grid: HexGrid,
  score: Float64Array,
  border: Uint8Array,
  interior: number,
  target: number,
): { land: Uint8Array; lost: number } {
  const n = grid.count;
  const sorted: number[] = [];
  for (let h = 0; h < n; h++) if (!border[h]) sorted.push(score[h]);
  sorted.sort((a, b) => b - a);
  let want = target;
  let land = new Uint8Array(n);
  let lost = 0;
  for (let iter = 0; iter < 4; iter++) {
    const k = Math.max(1, Math.min(interior - 1, Math.round(want * n)));
    const threshold = sorted[k];
    land = new Uint8Array(n);
    let raw = 0;
    for (let h = 0; h < n; h++) {
      if (!border[h] && score[h] > threshold) {
        land[h] = 1;
        raw++;
      }
    }
    fillPuddles(grid, land, border);
    const kept = keepLargest(grid, land);
    lost = raw > 0 ? Math.max(0, raw - kept) / raw : 0;
    const frac = kept / n;
    if (Math.abs(frac - target) < 0.006) break;
    want += target - frac;
  }
  return { land, lost };
}

/** Connected components of hexes where mask[h] === value. Returns labels (-1 elsewhere) and sizes. */
function components(grid: HexGrid, mask: Uint8Array, value: number): { label: Int32Array; sizes: number[] } {
  const n = grid.count;
  const label = new Int32Array(n).fill(-1);
  const sizes: number[] = [];
  const stack: number[] = [];
  for (let s = 0; s < n; s++) {
    if (mask[s] !== value || label[s] !== -1) continue;
    const id = sizes.length;
    let size = 0;
    label[s] = id;
    stack.push(s);
    while (stack.length > 0) {
      const h = stack.pop()!;
      size++;
      for (let d = 0; d < 6; d++) {
        const m = grid.neighbours[h * 6 + d];
        if (m >= 0 && mask[m] === value && label[m] === -1) {
          label[m] = id;
          stack.push(m);
        }
      }
    }
    sizes.push(size);
  }
  return { label, sizes };
}

function fillPuddles(grid: HexGrid, land: Uint8Array, border: Uint8Array): void {
  const { label, sizes } = components(grid, land, 0);
  const touchesBorder = new Uint8Array(sizes.length);
  for (let h = 0; h < grid.count; h++) if (border[h] && label[h] >= 0) touchesBorder[label[h]] = 1;
  for (let h = 0; h < grid.count; h++) {
    const c = label[h];
    if (c >= 0 && !touchesBorder[c] && sizes[c] <= MAX_PUDDLE) land[h] = 1;
  }
}

/** Sinks every land component except the largest; returns the remaining land count. */
function keepLargest(grid: HexGrid, land: Uint8Array): number {
  const { label, sizes } = components(grid, land, 1);
  if (sizes.length === 0) return 0;
  let best = 0;
  for (let i = 1; i < sizes.length; i++) if (sizes[i] > sizes[best]) best = i;
  for (let h = 0; h < grid.count; h++) if (land[h] && label[h] !== best) land[h] = 0;
  return sizes[best];
}

/**
 * Writes water terrain: water enclosed by land becomes Lake when small; all other water is sea —
 * Shallows within one hex of the coast (two where the shelf noise says so), deep Ocean beyond.
 */
function classifyWater(ctx: MapContext, land: Uint8Array, border: Uint8Array, shelf: Noise): void {
  const { grid, n, terrain, ux, uy } = ctx;
  const { label, sizes } = components(grid, land, 0);
  const open = new Uint8Array(sizes.length);
  for (let h = 0; h < n; h++) if (border[h]) open[label[h]] = 1;
  const landHexes: number[] = [];
  for (let h = 0; h < n; h++) if (land[h]) landHexes.push(h);
  const water = new Uint8Array(n);
  for (let h = 0; h < n; h++) water[h] = land[h] ? 0 : 1;
  const { dist } = bfs(grid, landHexes, water);
  for (let h = 0; h < n; h++) {
    if (land[h]) {
      terrain[h] = Terrain.Plains;
      continue;
    }
    const c = label[h];
    if (!open[c] && sizes[c] <= MAX_ENCLOSED_LAKE) terrain[h] = Terrain.Lake;
    else if (dist[h] === 1 || (dist[h] === 2 && shelf.fbm(ux[h] * 0.2, uy[h] * 0.2, 2) > 0.1)) {
      terrain[h] = Terrain.Shallows;
    } else terrain[h] = Terrain.Ocean;
  }
}
